import React, { useEffect, useMemo, useState } from "react";
import { ClientDetails } from "src/interfaces/IClient";
import { Device } from "src/interfaces/IDevice";
import { AlenRoom, Floor, Zone } from "src/interfaces/IZone";
import { Property } from "src/interfaces/IProperty";
import useAlenDevices from "src/services/alen/useAlenDevices";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import DeviceSelectionForm from "./DeviceSelectionForm";
import useSelectorOptions from "./useSelectorOptions";
import { useDeviceQuery } from "./DataFetching.ts/device";
import { SelectedValue } from "./types";

export type ComponentOptions = {
  hideProperty?: boolean;
};

type Options = ComponentOptions & {
  controlledValue: SelectedValue;
};

type DeviceSelectorReturn = {
  devices: Device[];
  selectedZone: Zone | undefined;
  alenDevices: AlenDevice[];
  properties: Property[];
  property?: Property;
  zone: Zone | undefined;
  zones: Zone[];
  floors: Floor[];
  selectedFloors: Floor[];
  isLoading: {
    property: boolean;
    zone: boolean;
    device: boolean;
    alenDevices: boolean;
  };
  formElement: JSX.Element;
  refetchZone: () => Promise<void>;
  refetchDevice: () => Promise<void>;
  refetchAlenDevices: () => Promise<void>;
};

export default function useDeviceSelector(
  client: ClientDetails,
  options?: Options
): DeviceSelectorReturn {
  const [selected, setSelected] = useState<SelectedValue>({
    propertyName: options?.controlledValue.propertyName,
    zoneId: options?.controlledValue.zoneId,
    floors: [],
  });

  const {
    loading: loadingOptions,
    data: optionsData,
    refetchZone,
  } = useSelectorOptions({
    client,
    selected,
  });

  useEffect(() => {
    setSelected({
      propertyName: options?.controlledValue.propertyName,
      zoneId: options?.controlledValue.zoneId,
      floors: [],
    });
  }, [options?.controlledValue.propertyName, options?.controlledValue.zoneId]);

  const wyndDevices = useDeviceQuery(client, optionsData.zones);
  const selectedProperty = optionsData.properties.find(
    (p) => p.property_name === selected.propertyName
  );

  const alenRooms: AlenRoom[] | null = useMemo(
    () =>
      selected.floors.length === 0
        ? null
        : selected.floors
            .filter((f) => f.alenId !== undefined)
            .map((f) => ({ room_id: f.alenId as number, name: f.name })),
    [selected.floors]
  );

  const alenDevices = useAlenDevices(selectedProperty, alenRooms);
  const formElement = (
    <DeviceSelectionForm
      hideProperty={options?.hideProperty}
      options={optionsData}
      selected={selected}
      setSelected={setSelected}
    />
  );

  return {
    devices: wyndDevices.data,
    selectedZone: selected.zoneId
      ? optionsData.zones.find((z) => z.zone_id === selected.zoneId)
      : undefined,
    alenDevices: alenDevices.data ?? [],
    properties: optionsData.properties,
    property: selectedProperty,
    zone: optionsData.zones.find((z) => z.zone_id === selected.zoneId),
    zones: optionsData.zones,
    floors: optionsData.floors,
    selectedFloors: selected.floors,
    isLoading: {
      property: loadingOptions,
      zone: loadingOptions,
      device: wyndDevices.isLoading,
      alenDevices: alenDevices.loading,
    },
    formElement,
    refetchZone,
    refetchAlenDevices: () => alenDevices.refetch() as Promise<void>,
    refetchDevice: () => wyndDevices.refetch() as Promise<void>,
  };
}
